import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ShieldCheck, ClipboardCheck, Siren, FileText, Search, AlertTriangle, RotateCcw } from "lucide-react";
import { Header } from "@/components/Header";
import { UploadZone } from "@/components/UploadZone";
import { AnalyzingState } from "@/components/AnalyzingState";
import { GateMessage } from "@/components/GateMessage";
import { ResultsView } from "@/components/ResultsView";
import { AddonReviewModal } from "@/components/AddonReviewModal";
import { PaywallModal } from "@/components/PaywallModal";
import { analyzeContract, regenerateBuyerScript } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import featureFrame from "@/assets/feature-frame.png";

const RESULT_KEY = "cc_result";
const ADDONS_KEY = "cc_addons";

// One shared neon frame (empty bar + gold hexagon) with the gold icon and metallic label overlaid,
// so every feature bar is pixel-identical to the reference. Aspect locked to the frame image.
const Feature = ({ icon, title, delay }) => (
  <div
    className={`group relative w-full ${delay !== undefined ? "animate-fade-up" : ""}`}
    style={{ aspectRatio: "1187 / 283", ...(delay !== undefined ? { animationDelay: delay } : {}) }}
  >
    <img
      src={featureFrame}
      alt=""
      aria-hidden="true"
      draggable="false"
      className="pointer-events-none absolute inset-0 h-full w-full select-none"
    />
    {/* gold icon centered inside the hexagon */}
    <div
      className="absolute flex items-center justify-center text-primary transition-transform duration-300 group-hover:scale-110"
      style={{ left: "11.9%", top: "50%", height: "42%", aspectRatio: "1 / 1", transform: "translate(-50%, -50%)" }}
      aria-hidden="true"
    >
      {icon}
    </div>
    {/* metallic silver label inside the bar */}
    <div className="absolute inset-y-0 flex items-center" style={{ left: "27%", right: "7%" }}>
      <span
        className="text-chrome-bright font-heading text-xs font-800 uppercase leading-tight tracking-tight sm:text-sm md:text-base lg:text-lg"
        style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.7))" }}
      >
        {title}
      </span>
    </div>
  </div>
);

const BUBBLES = [
  {
    title: "Advertised-Price Check",
    // magnifying glass scanning a piece of paper
    icon: (
      <span className="relative block h-full w-full">
        <FileText className="absolute inset-0 h-full w-full text-primary" strokeWidth={1.8} />
        <Search className="animate-mag-scan absolute bottom-0 right-0 h-1/2 w-1/2 text-primary" strokeWidth={2.4} />
      </span>
    ),
  },
  {
    title: "Hidden-Fee Audit",
    icon: <Siren className="animate-icon-flash h-full w-full text-primary" strokeWidth={1.8} />,
  },
  {
    title: "Mandatory Disclosure Check",
    icon: <ClipboardCheck className="animate-check-pop h-full w-full text-primary" strokeWidth={1.8} />,
  },
  {
    title: "We Don't Store Your Data",
    icon: <ShieldCheck className="animate-shield-pulse h-full w-full text-primary" strokeWidth={1.8} />,
  },
];

const LENS = 210;
const ZOOM = 1.9;

// The four selling-point cards, with a live magnifying-glass lens: while the pointer is inside this
// area the real cursor is hidden and a chrome/gold lens follows it, rendering a scaled duplicate of the
// cards aligned to the point underneath — so the buyer literally inspects the fine print. Confined to
// this container only.
const MagnifierBubbles = () => {
  const ref = useRef(null);
  const [lens, setLens] = useState(null); // { x, y, w, h } relative to container, or null
  // Touch / coarse-pointer devices (phones, tablets) have no hovering cursor — the magnifier makes no
  // sense there and `cursor: none` would just hide nothing, so we render plain cards instead.
  const [magnifyOn, setMagnifyOn] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setMagnifyOn(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const handleMove = (e) => {
    if (!magnifyOn) return;
    const r = ref.current.getBoundingClientRect();
    setLens({ x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height });
  };

  return (
    <div
      ref={ref}
      data-testid="feature-bubbles"
      onMouseMove={magnifyOn ? handleMove : undefined}
      onMouseLeave={magnifyOn ? () => setLens(null) : undefined}
      className="relative flex w-full flex-col gap-3 lg:w-[90%] lg:col-span-5"
      style={{ cursor: magnifyOn && lens ? "none" : "default" }}
    >
      {BUBBLES.map((b, i) => (
        <Feature key={b.title} {...b} delay={`${60 + i * 80}ms`} num={i + 1} />
      ))}

      {magnifyOn && lens && (
        <>
          {/* the lens: a circular window magnifying a duplicate of the cards */}
          <div
            data-testid="magnifier-lens"
            className="pointer-events-none absolute z-40 overflow-hidden rounded-full bg-background"
            style={{
              left: lens.x - LENS / 2,
              top: lens.y - LENS / 2,
              width: LENS,
              height: LENS,
              boxShadow:
                "0 0 0 4px rgba(234,179,8,0.9), 0 0 0 8px rgba(148,163,184,0.45), 0 16px 38px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: lens.w,
                height: lens.h,
                transformOrigin: "top left",
                transform: `translate(${LENS / 2 - lens.x * ZOOM}px, ${LENS / 2 - lens.y * ZOOM}px) scale(${ZOOM})`,
              }}
            >
              <div className="flex flex-col gap-3.5" style={{ height: lens.h }}>
                {BUBBLES.map((b) => (
                  <Feature key={b.title} {...b} />
                ))}
              </div>
            </div>
            {/* glass glare */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 30% 26%, rgba(255,255,255,0.22), rgba(255,255,255,0) 46%)",
              }}
            />
          </div>
          {/* the handle */}
          <div
            className="pointer-events-none absolute z-30"
            style={{
              left: lens.x + LENS * 0.32,
              top: lens.y + LENS * 0.32,
              width: 54,
              height: 14,
              borderRadius: 8,
              transform: "rotate(45deg)",
              background: "linear-gradient(180deg,#f1f5f9,#94a3b8 60%,#64748b)",
              boxShadow: "0 3px 9px rgba(0,0,0,0.55)",
            }}
          />
        </>
      )}
    </div>
  );
};

// Generic desktop-only magnifying-glass wrapper. Renders `children` normally, and — only when the device
// has a hovering fine pointer — shows a chrome/gold lens that magnifies a scaled duplicate (`zoomContent`)
// aligned to the point under the cursor. Used for the legal disclaimer so buyers can inspect the fine print.
const Magnifier = ({ children, zoomContent, rootClassName = "", testId, lensSize = LENS, zoom = ZOOM }) => {
  const ref = useRef(null);
  const [lens, setLens] = useState(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setOn(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    setLens({ x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height });
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      onMouseMove={on ? handleMove : undefined}
      onMouseLeave={on ? () => setLens(null) : undefined}
      className={`relative ${rootClassName}`}
      style={{ cursor: on && lens ? "none" : "default" }}
    >
      {children}
      {on && lens && (
        <>
          <div
            data-testid={testId ? `${testId}-lens` : undefined}
            className="pointer-events-none absolute z-40 overflow-hidden rounded-full bg-background"
            style={{
              left: lens.x - lensSize / 2,
              top: lens.y - lensSize / 2,
              width: lensSize,
              height: lensSize,
              boxShadow:
                "0 0 0 4px rgba(234,179,8,0.9), 0 0 0 8px rgba(148,163,184,0.45), 0 16px 38px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: lens.w,
                height: lens.h,
                transformOrigin: "top left",
                transform: `translate(${lensSize / 2 - lens.x * zoom}px, ${lensSize / 2 - lens.y * zoom}px) scale(${zoom})`,
              }}
            >
              {zoomContent}
            </div>
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 30% 26%, rgba(255,255,255,0.22), rgba(255,255,255,0) 46%)",
              }}
            />
          </div>
          <div
            className="pointer-events-none absolute z-30"
            style={{
              left: lens.x + lensSize * 0.32,
              top: lens.y + lensSize * 0.32,
              width: 54,
              height: 14,
              borderRadius: 8,
              transform: "rotate(45deg)",
              background: "linear-gradient(180deg,#f1f5f9,#94a3b8 60%,#64748b)",
              boxShadow: "0 3px 9px rgba(0,0,0,0.55)",
            }}
          />
        </>
      )}
    </div>
  );
};

const LegalDisclaimer = () => (
  <div data-testid="legal-disclaimer" className="max-w-4xl space-y-2 leading-relaxed text-muted-foreground" style={{ fontSize: "12px" }}>
    <p>
      <span className="font-700 text-foreground">LIMITATION OF LIABILITY:</span> THIS APP IS NOT
      AFFILIATED WITH THE ONTARIO MOTOR VEHICLE INDUSTRY COUNCIL OR SERVICE ONTARIO. THE DEVELOPERS AND
      OPERATORS OF THIS PLATFORM ACCEPT ZERO LIABILITY FOR ANY FINANCIAL LOSSES, BAD VEHICLE PURCHASES
      OR UNEXPECTED LEGAL OBLIGATIONS RESULTING DIRECTLY OR INDIRECTLY FROM THE USE OR MISINTERPRETATION
      OF THIS TOOL. VERIFY YOUR CONTRACT WITH A LICENSED ONTARIO LEGAL PROFESSIONAL OR CONSUMER ADVOCATE
      BEFORE SIGNING.
    </p>
  </div>
);

export default function Scanner() {
  const { user, hasActivePass } = useAuth();
  const [state, setState] = useState("idle"); // idle | loading | review | done | error
  const [result, setResult] = useState(null);
  const [addonDecisions, setAddonDecisions] = useState({});
  const [errorMsg, setErrorMsg] = useState("");

  // Restore an in-progress report after an OAuth / Stripe redirect so the scan isn't lost mid-paywall.
  useEffect(() => {
    const saved = sessionStorage.getItem(RESULT_KEY);
    if (saved) {
      try {
        setResult(JSON.parse(saved));
        setAddonDecisions(JSON.parse(sessionStorage.getItem(ADDONS_KEY) || "{}"));
        setState("done");
      } catch {
        sessionStorage.removeItem(RESULT_KEY);
      }
    }
  }, []);

  // Persist the completed report across redirects (login + payment).
  useEffect(() => {
    if (state === "done" && result && result.status === "analyzed") {
      sessionStorage.setItem(RESULT_KEY, JSON.stringify(result));
      sessionStorage.setItem(ADDONS_KEY, JSON.stringify(addonDecisions));
    }
  }, [state, result, addonDecisions]);

  const handleAnalyze = async (file) => {
    setState("loading");
    setResult(null);
    setAddonDecisions({});
    try {
      const data = await analyzeContract(file);
      setResult(data);
      const addons = data.status === "analyzed" ? data.optional_addons || [] : [];
      if (addons.length > 0) {
        setState("review");
      } else {
        setState("done");
        if (data.status === "analyzed") toast.success("Search complete");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "Invalid input").join(", ")
        : detail || err?.message || "Something went wrong. Please try again.";
      // A scanning error must NEVER lead to the paywall — clear any prior result and show a clear
      // "please resubmit" screen so the customer re-uploads before they can ever be asked to pay.
      sessionStorage.removeItem(RESULT_KEY);
      sessionStorage.removeItem(ADDONS_KEY);
      setResult(null);
      setErrorMsg(msg);
      setState("error");
    }
  };

  const handleReviewComplete = async (decisionsArray) => {
    const map = {};
    decisionsArray.forEach((d, i) => { map[i] = d; });
    setAddonDecisions(map);
    const acceptedIndices = decisionsArray.map((d, i) => (d === true ? i : null)).filter((i) => i !== null);
    try {
      const updated = await regenerateBuyerScript(result, acceptedIndices);
      setResult((r) => ({ ...r, ...updated }));
    } catch (e) {
      // If regeneration fails, fall back to the original script rather than blocking the report.
      console.warn("Buyer-script regeneration failed; keeping original script.", e);
    }
    setState("done");
  };

  const reset = () => {
    sessionStorage.removeItem(RESULT_KEY);
    sessionStorage.removeItem(ADDONS_KEY);
    setState("idle");
    setResult(null);
    setAddonDecisions({});
  };

  const isGate = result && result.status !== "analyzed";
  // Paywall gate: server flags non-partner scans as requires_payment. A logged-in viewer with an active
  // 15-day pass unlocks all reports. HARD GUARD: only a genuine "analyzed" result can ever be gated — a
  // scan error or gate (vin_missing / not_a_contract / new_car) must never reach the paywall.
  const gated = !!(result && result.status === "analyzed" && result.requires_payment && !hasActivePass);

  return (
    <div className="relative min-h-screen bg-background">
      <div
        className="pointer-events-none fixed inset-x-0 top-0 h-[420px] bg-gradient-to-b from-secondary/70 via-secondary/20 to-transparent"
        aria-hidden="true"
      />
      <Header />

      <main className="relative mx-auto max-w-[1400px] px-5 py-8 sm:px-8 sm:py-12">
        {state === "idle" && (
          <div className="grid grid-cols-1 gap-x-10 gap-y-6 lg:grid-cols-12">
            {/* Content row: four selling-point cards (left) exactly match the upload box height (right) */}
            <MagnifierBubbles />
            <div className="flex lg:col-span-7">
              <UploadZone onAnalyze={handleAnalyze} loading={false} />
            </div>
          </div>
        )}

        {state === "loading" && <AnalyzingState />}

        {state === "error" && (
          <div data-testid="scan-error" className="mx-auto flex max-w-lg flex-col items-center rounded-2xl border border-destructive/40 bg-card p-8 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-destructive/15 ring-1 ring-destructive/40">
              <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
            </div>
            <h2 className="font-heading text-2xl font-800 uppercase tracking-wide text-foreground">
              We couldn't read that document
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {errorMsg || "Something went wrong while scanning."} Please upload a clearer photo or PDF of
              the full Bill of Sale and try again.
            </p>
            <button
              data-testid="scan-error-retry"
              onClick={reset}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-700 text-primary-foreground transition-opacity hover:opacity-90"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Upload another file
            </button>
          </div>
        )}

        {state === "review" && (
          <AddonReviewModal addons={result.optional_addons || []} onComplete={handleReviewComplete} />
        )}

        {state === "done" && isGate && <GateMessage result={result} onReset={reset} />}
        {state === "done" && !isGate && (
          <div className="relative">
            <div
              data-testid="report-container"
              className={gated ? "pointer-events-none max-h-[80vh] select-none overflow-hidden blur-[7px]" : ""}
              aria-hidden={gated ? "true" : undefined}
            >
              <ResultsView result={result} addonDecisions={addonDecisions} onReset={reset} />
            </div>
            {gated && (
              <>
                <div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-background/40 via-background/70 to-background" />
                <PaywallModal />
              </>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-border bg-secondary/40">
        <div className="mx-auto max-w-[1400px] px-5 py-8 sm:px-8">
          <Magnifier testId="legal-magnifier" zoomContent={<LegalDisclaimer />}>
            <LegalDisclaimer />
          </Magnifier>
        </div>
      </footer>
    </div>
  );
}
